import { useCallback, useEffect, useMemo, useRef } from 'react';
import { v4 as uuid } from 'uuid';

import useViewportSpy from '@monofront/business/hooks/web/useViewportSpy';
import { trackEvent } from '@monofront/tracking';
import useI18n from '@hooks/i18n/useI18n';

// Same threshold as the app (useIsOnScreen), so that a "view" means the same thing on both platforms.
const VIEW_ITEM_LIST_VISIBLE_THRESHOLD = 0.5;

type SelectItemEventData = {
  product: {
    sku: string;
    name: string;
    index: number;
  };
};

type RecommendationTrackingArgs = {
  trackingId: string;
  // view_item_list is only sent when enabled: it is opted in per carousel from Prismic.
  isViewItemListEnabled?: boolean;
  products?: { sku: string; title: string }[];
};

type RecommendationTrackingReturn = {
  sendSelectItemEvent: (eventData: SelectItemEventData) => void;
  ref: (node?: Element | null) => void;
};

export const useRecommendationTracking = ({
  trackingId,
  isViewItemListEnabled = false,
  products = [],
}: RecommendationTrackingArgs): RecommendationTrackingReturn => {
  const impressionIdRef = useRef(uuid());
  const { currency } = useI18n();

  const sendSelectItemEvent = useCallback(
    (eventData: SelectItemEventData) => {
      trackEvent({
        type: 'LFSelectItem',
        data: {
          item_list_id: trackingId,
          item_list_impression_id: impressionIdRef.current,
          selected_item: {
            item_id: eventData.product.sku,
            item_name: eventData.product.name,
            index: eventData.product.index,
          },
        },
      });
    },
    [trackingId],
  );

  const { ref, isVisible } = useViewportSpy({
    threshold: VIEW_ITEM_LIST_VISIBLE_THRESHOLD,
    freezeOnceVisible: true,
  });

  const items = useMemo(
    () =>
      products.map((product, index) => ({
        item_id: product.sku,
        item_name: product.title,
        index,
      })),
    [products],
  );

  const isReadyToView = isViewItemListEnabled && items.length > 0;

  useEffect(() => {
    if (!isReadyToView || !isVisible) return;

    trackEvent({
      type: 'LFViewItemList',
      data: {
        currency,
        item_list_id: trackingId,
        item_list_impression_id: impressionIdRef.current,
        items,
      },
    });
  }, [isReadyToView, isVisible]);

  return { sendSelectItemEvent, ref };
};
